import './bootstrap';
import '../css/app.css';
import 'vue-sonner/style.css'

import { createApp, h } from 'vue';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { installChurchActivityTracking } from './plugins/churchActivity';
import axios from 'axios';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue, route } from 'ziggy-js';
import { Ziggy as ZiggyConfig } from './ziggy.js';

const Ziggy = { ...ZiggyConfig, url: typeof window !== 'undefined' ? window.location.origin : ZiggyConfig.url };

window.route = (name, params, absolute) => route(name, params, absolute, Ziggy);

import { toast } from 'vue-sonner';
import GtagPlugin from './plugins/gtag'
import BingUetPlugin from './plugins/bingUet'
import MetaPixelPlugin from './plugins/metaPixel'
import AmplitudePlugin from './plugins/amplitude'


import 'vue3-toastify/dist/index.css';

let vfmInstallPromise = null;
let tippyInstallPromise = null;

const runWhenIdle = (callback, timeout = 3000) => {
    if (import.meta.env.SSR) {
        return;
    }

    if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => {
            callback();
        }, { timeout });
        return;
    }

    window.setTimeout(callback, timeout);
};

const loadOnInteractionOrDelay = (callback, delay = 3000) => {
    if (import.meta.env.SSR) return;
    let done = false;
    let timer = null;
    const events = ['click', 'touchstart', 'mousemove', 'keydown', 'wheel'];
    const run = () => {
        if (done) return;
        done = true;
        if (timer) clearTimeout(timer);
        events.forEach(e => removeEventListener(e, run, { capture: true }));
        callback();
    };
    timer = setTimeout(run, delay);
    events.forEach(e => addEventListener(e, run, { once: true, capture: true, passive: true }));
};

const installVueFinalModal = async (app) => {
    if (import.meta.env.SSR || app.config.globalProperties.__jcbVfmInstalled) {
        return;
    }

    if (!vfmInstallPromise) {
        vfmInstallPromise = Promise.all([
            import('vue-final-modal'),
            import('vue-final-modal/style.css'),
        ])
            .then(([vfmModule]) => {
                app.use(vfmModule.createVfm());
                app.config.globalProperties.__jcbVfmInstalled = true;
            })
            .catch((error) => {
                console.error('Failed to initialize Vue Final Modal', error);
            })
            .finally(() => {
                vfmInstallPromise = null;
            });
    }

    await vfmInstallPromise;
};

const installVueTippy = async (app) => {
    if (import.meta.env.SSR || app.config.globalProperties.__jcbTippyInstalled) {
        return;
    }

    if (!tippyInstallPromise) {
        tippyInstallPromise = Promise.all([
            import('vue-tippy'),
            import('tippy.js/dist/tippy.css'),
        ])
            .then(([tippyModule]) => {
                app.use(tippyModule.default, {
                    directive: 'tippy',
                    component: 'tippy',
                    componentSingleton: 'tippy-singleton',
                    defaultProps: {
                        placement: 'auto-end',
                        allowHTML: true,
                    },
                });
                app.config.globalProperties.__jcbTippyInstalled = true;
            })
            .catch((error) => {
                console.error('Failed to initialize Vue Tippy', error);
            })
            .finally(() => {
                tippyInstallPromise = null;
            });
    }

    await tippyInstallPromise;
};

createInertiaApp({
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });
        const initialComponent = props.initialPage.component;
        const isHomePage = initialComponent === 'Home';

        app.use(plugin);
        app.use(ZiggyVue, Ziggy);
        app.use(toast);

        if (!import.meta.env.SSR) {
            if (isHomePage) {
                runWhenIdle(() => {
                    void installVueFinalModal(app);
                    void installVueTippy(app);
                }, 8000);
            } else {
                loadOnInteractionOrDelay(() => {
                    void installVueFinalModal(app);
                    void installVueTippy(app);
                }, 2000);
            }

            document.addEventListener('inertia:navigate', (event) => {
                if (event.detail.page.component !== 'Home') {
                    void installVueFinalModal(app);
                    void installVueTippy(app);
                }
            });
        }

        // Install analytics plugins synchronously (sets up queues + provide/inject only)
        // External scripts are loaded sequentially below via runWhenIdle
        if (!import.meta.env.SSR && props.initialPage.props.google?.enabled) {
            app.use(GtagPlugin, {
                ga_measurement_id: props.initialPage.props.google.analytics_id,
                ads_id: props.initialPage.props.google.ads_id,
                enabled: props.initialPage.props.google.enabled,
                autoLoad: false,
            });
        }

        if (!import.meta.env.SSR && props.initialPage.props.bing?.enabled) {
            app.use(BingUetPlugin, {
                uet_tag_id: props.initialPage.props.bing.uet_tag_id,
                enabled: props.initialPage.props.bing.enabled,
                autoLoad: false,
            });
        }

        if (!import.meta.env.SSR && props.initialPage.props.meta?.enabled) {
            app.use(MetaPixelPlugin, {
                pixel_id: props.initialPage.props.meta.pixel_id,
                enabled: props.initialPage.props.meta.enabled,
                autoLoad: false,
            });
        }

        if (!import.meta.env.SSR && props.initialPage.props.amplitude?.enabled) {
            app.use(AmplitudePlugin, {
                enabled: props.initialPage.props.amplitude.enabled,
            });
        }

        // Load PostHog immediately (all visitors) so funnel pageviews and
        // bootstrapped experiment flags apply from first paint; session
        // recording still starts only for authenticated users.
        if (!import.meta.env.SSR) {
            if (props.initialPage.props.posthog?.enabled) {
                void import('./plugins/posthog')
                    .then(({ loadPostHog }) => loadPostHog(
                        props.initialPage.props.posthog,
                        props.initialPage.props.auth?.user,
                    ))
                    .catch((error) => {
                        console.error('Failed to load PostHog plugin', error);
                    });
            } else {
                // Stops usePostHog from queueing events that will never flush
                window.__posthogDisabled = true;
            }

            // Load the remaining external analytics scripts sequentially to prevent
            // simultaneous main-thread blocking.
            loadOnInteractionOrDelay(async () => {
                if (props.initialPage.props.google?.enabled) {
                    const { loadGtagScript } = await import('./plugins/gtag');
                    await loadGtagScript(props.initialPage.props.google.analytics_id);
                }
                if (props.initialPage.props.meta?.enabled) {
                    const { loadMetaPixelScript } = await import('./plugins/metaPixel');
                    await loadMetaPixelScript();
                }
                if (props.initialPage.props.bing?.enabled) {
                    const { loadBingUetScript } = await import('./plugins/bingUet');
                    await loadBingUetScript(props.initialPage.props.bing.uet_tag_id);
                }
                if (props.initialPage.props.amplitude?.enabled) {
                    const { loadAmplitude } = await import('./plugins/amplitude');
                    await loadAmplitude(
                        props.initialPage.props.amplitude.api_key,
                        props.initialPage.props.auth?.user,
                    );
                }
            }, isHomePage ? 5000 : 3000);
        }

        // Initialize Sentry for error tracking (client-side only)
        if (!import.meta.env.SSR) {
            runWhenIdle(async () => {
                try {
                    const Sentry = await import('@sentry/vue');

                    Sentry.init({
                        app,
                        dsn: 'https://43a1ad93544591f4f580662125f3e5fe@o1184402.ingest.us.sentry.io/4510862151057408',
                        integrations: [
                            Sentry.browserTracingIntegration(),
                            Sentry.replayIntegration({
                                maskAllText: false,
                                blockAllMedia: false,
                            }),
                        ],
                        tracesSampleRate: 0.1,
                        replaysSessionSampleRate: 0.0,
                        replaysOnErrorSampleRate: 1.0,
                        denyUrls: [/^iabjs:\/\//],
                    });
                } catch (error) {
                    console.error('Failed to initialize Sentry', error);
                }
            }, 7000);
        }

        if (!import.meta.env.SSR) {
            installChurchActivityTracking({
                router,
                initialPage: props.initialPage,
                axios,
                window,
                document,
            });
        }

        app.mount(el);
        return app;
    },
    progress: {
        color: '#4B5563',
    },
});

// Hide HelpScout Beacon on conversations page
document.addEventListener('inertia:navigate', (event) => {
    const currentPage = event.detail.page.component;

    if (window.Beacon) {
        if (currentPage === 'Conversations/Index') {
            // Hide on conversations page
            window.Beacon('close');
            window.Beacon('config', { display: { style: 'manual' } });
        } else {
            // Show on all other pages
            window.Beacon('config', { display: { style: 'icon' } });
        }
    }
});
